<template>
  <div data-testid="market-overview-choice" class="flex flex-col gap-1">
    <div class="flex w-75 items-baseline justify-between gap-2">
      <label :id="labelId" class="text-xs font-medium text-gray-600">
        {{ $t('messages.detail.marketOverview.label') }}
      </label>
      <!-- Always rendered, hidden while nothing is overridden: toggling it must
           not move the Select. -->
      <button
        type="button"
        data-testid="market-overview-restore"
        class="inline-flex cursor-pointer items-center gap-1 text-xs text-primary-600 hover:text-primary-800 hover:underline"
        :class="{ invisible: !overridden }"
        :aria-hidden="!overridden"
        :tabindex="overridden ? 0 : -1"
        :disabled="!overridden || saving"
        @click="restore"
      >
        <span class="pi pi-bolt text-[10px]" />
        {{ $t('messages.detail.marketOverview.restore') }}
      </button>
    </div>
    <Select
      v-model="model"
      data-testid="market-overview-select"
      class="w-75"
      :options="choice.options"
      option-label="label"
      option-value="value"
      option-group-label="label"
      option-group-children="items"
      filter
      reset-filter-on-hide
      :filter-placeholder="$t('messages.detail.marketOverview.filterPlaceholder')"
      :placeholder="$t('messages.detail.marketOverview.placeholder')"
      :aria-labelledby="labelId"
      :loading="saving"
      :disabled="saving"
      :invalid="Boolean(errorCode)"
    >
      <template #option="{ option }">
        <div class="flex w-full items-center justify-between gap-3">
          <span>{{ option.label }}</span>
          <span class="flex items-center gap-2 text-xs text-gray-500">
            <span
              v-if="option.value === choice.auto?.value"
              class="inline-flex items-center gap-1 text-primary-600"
            >
              <span class="pi pi-bolt text-[10px]" />{{ $t('messages.detail.marketOverview.autoTag') }}
            </span>
            {{ option.period }}
          </span>
        </div>
      </template>
    </Select>
    <p
      data-testid="market-overview-hint"
      class="flex h-4 min-w-0 items-center gap-1 text-xs"
      :class="[errorCode ? 'text-red-600' : 'text-gray-500', { invisible: !hint }]"
      :title="hint"
    >
      <span class="shrink-0 text-[10px]" :class="hintIcon" />
      <!-- Only the register's own wording shrinks: EHR purpose names run long
           ("Muu kolme või enama korteriga elamu"), and the line must still end
           on the purpose it led to. The full sentence is in the tooltip. -->
      <i18n-t
        v-if="showsAutoReason"
        keypath="messages.detail.marketOverview.reasonAuto"
        tag="span"
        scope="global"
        class="flex min-w-0 whitespace-nowrap"
      >
        <template #place>{{ autoReasonParts.place }}</template>
        <template #county>{{ autoReasonParts.county }}</template>
        <template #kind><span class="mx-1 min-w-0 truncate">{{ autoReasonParts.kind }}</span></template>
        <template #purpose>{{ autoReasonParts.purpose }}</template>
      </i18n-t>
      <span v-else class="truncate">{{ hint }}</span>
    </p>
  </div>
</template>

<script setup>
import { computed, shallowRef, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMarketOverviewChoice } from '@/composables/useMarketOverviewChoice'

/**
 * RED-317 — which Turuülevaade a universal mall's §4.2 gets. Pre-set to the
 * automatic match; the evaluator can pick another published cell of the
 * company or restore the automatic one. The hint line says why the automatic
 * match is what it is (Luise tn 2: EHR calls the office an apartment building).
 */
const { t } = useI18n()
const { choice, saving, errorCode, save } = useMarketOverviewChoice()
const labelId = `market-overview-label-${useId()}`

/** What the server will export: a published stored choice, else the automatic match. */
const serverValue = computed(() => {
  if (choice.value.selected?.available) {
    return choice.value.selected.value
  }
  return choice.value.auto.available ? choice.value.auto.value : null
})

// The evaluator's pick while it is being saved, and after a failed save —
// never dropped back to the old value on an error.
const picked = shallowRef(null)

const overridden = computed(() => choice.value.selected !== null)

const optionFor = value => choice.value.options
  .flatMap(group => group.items)
  .find(item => item.value === value) ?? null

const persist = async (value, cell) => {
  picked.value = value
  if (await save(cell)) {
    picked.value = null
  }
}

const model = computed({
  get: () => picked.value ?? serverValue.value,
  set: value => {
    if (value === null || value === (picked.value ?? serverValue.value)) {
      return
    }
    // Picking the automatic cell is the same as restoring it: nothing stays
    // pinned when the registers later change.
    const cell = value === choice.value.auto.value ? null : optionFor(value)
    persist(value, cell && { county: cell.county, purpose: cell.purpose })
  },
})

const restore = () => persist(choice.value.auto.available ? choice.value.auto.value : null, null)

const autoReasonParts = computed(() => {
  const { source, detail, place, county, purpose } = choice.value.auto.reason
  return {
    place: place ?? t('messages.detail.marketOverview.unknownPlace'),
    county,
    kind: detail && source !== 'template'
      ? detail.toLocaleLowerCase('et')
      : t(`messages.detail.marketOverview.source.${source}`),
    purpose,
  }
})

/** The automatic match's grounds are the hint while nothing else needs saying. */
const showsAutoReason = computed(() => !errorCode.value
  && choice.value.selected === null
  && choice.value.auto.available)

const hint = computed(() => {
  const { auto, selected } = choice.value
  if (errorCode.value) {
    return t(`messages.detail.marketOverview.errors.${errorCode.value}`)
  }
  if (selected && !selected.available) {
    return t('messages.detail.marketOverview.staleChoice', { cell: selected.label })
  }
  if (selected) {
    return auto.label
      ? t('messages.detail.marketOverview.reasonManual', { auto: auto.label })
      : t('messages.detail.marketOverview.reasonManualNoAuto')
  }
  if (!auto.value) {
    return t('messages.detail.marketOverview.reasonNoCounty')
  }
  return auto.available
    ? t('messages.detail.marketOverview.reasonAuto', autoReasonParts.value)
    : t('messages.detail.marketOverview.reasonAutoMissing', { cell: auto.label })
})

const hintIcon = computed(() => {
  if (errorCode.value) return 'pi pi-exclamation-circle'
  if (choice.value.selected && !choice.value.selected.available) return 'pi pi-exclamation-triangle'
  return choice.value.selected ? 'pi pi-user' : 'pi pi-bolt'
})
</script>
